// Componente encargado de armar el formulario con las preguntas
// ============
// Importaciones de React
import React, { useEffect, useRef, useState } from 'react';
// Importaciones de Redux
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
// Importaciones de CSS
import classes from './Preguntas.css';
// Función para actualizar la encuesta
import { useDispatch } from 'react-redux';
import { enviarEncuesta } from '../../../store/actions/encuestas';
// Componentes complementarios
import PreguntaTexto from './PreguntaTexto/PreguntaTexto';
import PreguntaSiNo from './PreguntaSiNo/PreguntaSiNo';
import PreguntaMenu from './PreguntaMenu/PreguntaMenu';
import PreguntaMultiselect from './PreguntaMultiselect/PreguntaMultiselect';
import PreguntaEscala from './PreguntaEscala/PreguntaEscala';
import BotonEnviar from '../BotonEnviar/BotonEnviar';
import base from '../../../containers/Encuestas/EncuestasPrincipal.css';
// Componentes en desuso
//import PreguntaPuntaje from "./PreguntaPuntaje/PreguntaPuntaje";
//import PreguntaEstrella from "./PreguntaEstrella/PreguntaEstrella";
const getOpcionLabel = (op) => op?.opcion ?? op?.texto ?? op?.titulo ?? op?.nombre ?? op?.descripcion ?? '';

// Demora antes de grabar sola la respuesta que llega desde el mail. Le da tiempo a la página
// a estar realmente a la vista, para que un escáner de links de correo no registre un voto.
// Sólo demora el POST: la pantalla que ve la persona no espera esto.
const DEMORA_AUTO_ENVIO_MS = 1000;

// Pausa entre que se toca una opción y se pasa a la siguiente pantalla, para que se vea la elección
const DEMORA_AVANCE_MS = 250;

// Controles que se responden con un solo toque
const TIPOS_DE_OPCION = [1, 2, 4, 299];

const Preguntas = ({ interrogantes, datosCrudos }) => {
  const dispatch = useDispatch();

  interrogantes.forEach((obj) => {
    obj.obligatoria = obj.tipoControlId !== 3;
  });

  const hashEncuesta = useSelector((state) => state.encuestas.hashEncuesta);

  // ► Pantallas del formulario
  // Cada pantalla es una pregunta seguida de sus preguntas adicionales (vienen de la API en
  // `adicionales`). Las adicionales se guardan en `respuestas` con una clave propia, porque sus ids
  // son de otra tabla y pueden coincidir con los de las preguntas.
  const pantallas = interrogantes.map((p) => [
    p,
    ...(p.adicionales ?? []).map((a) => ({
      ...a,
      esAdicional: true,
      clave: `a-${p.id}-${a.id}`,
      encuestaPreguntaId: p.id,
      obligatoria: a.obligatoria === true
    }))
  ]);
  const totalPreguntas = pantallas.length;
  const totalPreguntasObligatorias = pantallas.flat().filter((obj) => obj.obligatoria).length;

  // Respuesta pre-seleccionada
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const preSeleccionada = queryParams.get('preSel');
  //console.log('Preseleccionada:', preSeleccionada);

  // ► Respuesta que llega ya elegida desde el mail
  // reclamoencuestas.prg manda ?hash=...&preSel=0..4, donde preSel es el índice de la opción
  // elegida dentro del array de opciones de la primera pregunta (invertido: el botón "1" del
  // mail manda preSel=4 y el "5" manda preSel=0).
  // Ojo: acá se valida la cadena cruda a propósito. Number(null) da 0, así que un link sin el
  // parámetro se tomaría como "eligió la primera opción".
  const primeraPregunta = interrogantes[0];
  const idxPreSel = /^\d+$/.test((preSeleccionada ?? '').trim()) ? Number(preSeleccionada) : NaN;
  const opcionPreSel = primeraPregunta?.opciones?.[idxPreSel];
  const preSelValido =
    Number.isInteger(idxPreSel) &&
    idxPreSel >= 0 &&
    idxPreSel <= 4 &&
    primeraPregunta?.tipoControlId === 2 &&
    !!opcionPreSel;

  // Mismo formato que emite PreguntaMultiselect al montarse, para que armarPayloadEncuesta
  // lo interprete igual (caso [[id, label]])
  const respuestaAuto = preSelValido
    ? {
        numeroPregunta: primeraPregunta.id,
        tieneContenido: true,
        contenido: [[opcionPreSel.id, getOpcionLabel(opcionPreSel)]],
        esObligatoria: primeraPregunta.tipoControlId !== 3
      }
    : null;

  // Si la primera respuesta vino del mail, esa pregunta no se muestra: se arranca en la siguiente
  const paginaMinima = preSelValido ? Math.min(1, Math.max(totalPreguntas - 1, 0)) : 0;

  // El estado arranca ya resuelto: la primera pregunta no se llega a ver ni un instante, y su
  // respuesta ya está cargada desde el primer render aunque el POST salga un segundo después.
  const [respuestas, setRespuestas] = useState(() => (respuestaAuto ? { [respuestaAuto.numeroPregunta]: respuestaAuto } : {}));
  const [respuestasRespondidas, setRespuestasRespondidas] = useState(() => (respuestaAuto?.esObligatoria ? 1 : 0));
  const [paginaActual, setPaginaActual] = useState(paginaMinima);

  const habilitarBotonEnviar = respuestasRespondidas === totalPreguntasObligatorias;

  const handleOnChangeRespuesta = (numeroPregunta, nuevaRespuesta) => {
    setRespuestas((prevRespuestas) => {
      const updatedRespuestas = {
        ...prevRespuestas,
        [numeroPregunta]: nuevaRespuesta
      };

      const respondidas = Object.values(updatedRespuestas).filter(
        (respuesta) => respuesta.tieneContenido === true && respuesta.esObligatoria === true
      ).length;

      setRespuestasRespondidas(respondidas);
      return updatedRespuestas;
    });
  };

  // ► Auto-envío de la primera respuesta
  // Graba lo que la persona eligió en el mail sin esperar que toque "Siguiente". El formulario ya
  // arranca en la pregunta siguiente; acá sólo se dispara el POST.
  const autoEnvioHecho = useRef(false);
  useEffect(() => {
    if (!preSelValido || autoEnvioHecho.current || navigator.webdriver) {
      return undefined;
    }

    let temporizador = null;
    let esperandoFoco = false;

    const enviarPrimeraRespuesta = () => {
      if (autoEnvioHecho.current) {
        return;
      }
      autoEnvioHecho.current = true;
      dispatch(
        enviarEncuesta({ [primeraPregunta.id]: respuestaAuto }, datosCrudos || '', hashEncuesta, { silencioso: true })
      );
    };

    const alVolverElFoco = () => {
      if (document.visibilityState === 'visible') {
        document.removeEventListener('visibilitychange', alVolverElFoco);
        esperandoFoco = false;
        enviarPrimeraRespuesta();
      }
    };

    temporizador = setTimeout(() => {
      if (document.visibilityState === 'visible') {
        enviarPrimeraRespuesta();
      } else {
        // La pestaña se abrió en segundo plano: esperamos a que alguien la mire
        esperandoFoco = true;
        document.addEventListener('visibilitychange', alVolverElFoco);
      }
    }, DEMORA_AUTO_ENVIO_MS);

    return () => {
      clearTimeout(temporizador);
      if (esperandoFoco) {
        document.removeEventListener('visibilitychange', alVolverElFoco);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  //console.log('Preguntas', interrogantes);
  //console.log('Respuestas', respuestas);
  //console.log('respuestasRespondidas', respuestasRespondidas);

  // ► Avance
  // El temporizador del avance lee las respuestas de una ref: cuando se dispara, el control ya
  // publicó la opción elegida y el closure tendría las respuestas viejas.
  const respuestasRef = useRef(respuestas);
  respuestasRef.current = respuestas;

  const avanzar = () => {
    setPaginaActual((pagina) => Math.min(pagina + 1, totalPreguntas - 1));
    dispatch(enviarEncuesta(respuestasRef.current, datosCrudos || '', hashEncuesta, { silencioso: true }));
  };

  // Una pantalla simple (una sola pregunta de opción, sin adicionales) pasa sola a la siguiente al
  // elegir. Se dispara desde el clic del control y no desde la respuesta, para que no avance al
  // montarse ni al volver con Anterior.
  const esUltimaPantalla = paginaActual + 1 === totalPreguntas;
  const pantallaActual = pantallas[paginaActual];
  const avanzaSola =
    !esUltimaPantalla && pantallaActual.length === 1 && TIPOS_DE_OPCION.includes(pantallaActual[0].tipoControlId);

  const temporizadorAvance = useRef(null);
  const programarAvance = () => {
    clearTimeout(temporizadorAvance.current);
    temporizadorAvance.current = setTimeout(avanzar, DEMORA_AVANCE_MS);
  };
  useEffect(() => () => clearTimeout(temporizadorAvance.current), [paginaActual]);

  // Una pantalla cuenta como respondida cuando tiene sus obligatorias; si no tiene, cuando se escribió algo
  const pantallaRespondida = (pantalla) => {
    const respondida = (pregunta) => respuestas[pregunta.clave ?? pregunta.id]?.tieneContenido === true;
    const obligatorias = pantalla.filter((pregunta) => pregunta.obligatoria);
    return obligatorias.length > 0 ? obligatorias.every(respondida) : pantalla.some(respondida);
  };
  const pantallasRespondidas = pantallas.filter(pantallaRespondida).length;

  // Se puede seguir cuando todas las obligatorias de la pantalla están respondidas
  const habilitarSiguiente = pantallaActual.every(
    (pregunta) => !pregunta.obligatoria || respuestas[pregunta.clave ?? pregunta.id]?.tieneContenido === true
  );

  // Arma el control que corresponde al tipo de la pregunta
  const renderControl = (pregunta) => {
    const id = pregunta.clave ?? pregunta.id;
    const respuesta = respuestas[id];
    // La respuesta de una adicional lleva los datos para grabarla en encuestasRespuestasAdicionales
    const alResponder = (r) =>
      handleOnChangeRespuesta(
        id,
        pregunta.esAdicional
          ? {
              ...r,
              esAdicional: true,
              encuestaPreguntaId: pregunta.encuestaPreguntaId,
              preguntaAdicionalId: pregunta.id,
              tipoControlId: pregunta.tipoControlId
            }
          : r
      );

    const onElegir = avanzaSola ? programarAvance : undefined;
    let valorInicial;

    switch (pregunta.tipoControlId) {
      case 1: //— Si/No
        valorInicial = respuesta?.id ?? pregunta.valorDefault;
        return (
          <PreguntaSiNo
            defaultValue={valorInicial}
            numeroPregunta={id}
            idSI={pregunta.opciones[0].id}
            idNO={pregunta.opciones[1].id}
            esObligatoria={pregunta.obligatoria}
            onChangeRespuesta={alResponder}
            onElegir={onElegir}
          />
        );

      case 299: //— Menú numérico
        valorInicial = respuesta?.contenido?.[0] ?? pregunta.valorDefault;
        return (
          <PreguntaMenu
            PreguntasOpciones={pregunta.opciones}
            valorDefault={valorInicial}
            numeroPregunta={id}
            esObligatoria={pregunta.obligatoria}
            onChangeRespuesta={alResponder}
            onElegir={onElegir}
          />
        );

      case 3: //— Texto libre
        valorInicial = respuesta?.contenido?.[1] ?? '';
        return (
          <PreguntaTexto
            id={`pregunta-${id}`}
            fraseSugerencia={pregunta.valorDefault}
            defaultValue={valorInicial}
            numeroPregunta={id}
            esObligatoria={pregunta.obligatoria}
            onChangeRespuesta={alResponder}
          />
        );

      case 4:
      case 2: {
        //— Escala 1 a 5 (tipo 2) y multiselect de selección única (tipo 4)
        // Base: lo ya respondido o el default de la pregunta
        let valorInicial = respuesta?.contenido ?? pregunta.valorDefault ?? [];

        // ► Reglas de pre-selección:
        // 1) preSeleccionada es 0..4
        // 2) estamos en la primera página
        // 3) el control actual es Multiselect (tipoControlId === 2)
        // 4) **AÚN no hay respuesta del usuario** (no tieneContenido)
        const idx = Number(preSeleccionada);
        const esEntero = Number.isInteger(idx);
        const cumpleRango = esEntero && idx >= 0 && idx <= 4;
        const aunSinContenido = !(respuesta?.tieneContenido === true);

        if (
          cumpleRango &&
          paginaActual === 0 &&
          !pregunta.esAdicional &&
          pregunta?.tipoControlId === 2 &&
          aunSinContenido
        ) {
          const opcion = pregunta?.opciones?.[idx];
          if (opcion) {
            // PreguntaMultiselect espera un array de pares [id, label]
            valorInicial = [[opcion.id, getOpcionLabel(opcion)]];
          }
        }

        if (pregunta.tipoControlId === 2) {
          return (
            <PreguntaEscala
              PreguntasOpciones={pregunta.opciones}
              valorDefault={valorInicial}
              numeroPregunta={id}
              esObligatoria={pregunta.obligatoria}
              onChangeRespuesta={alResponder}
              onElegir={onElegir}
            />
          );
        }

        return (
          <PreguntaMultiselect
            PreguntasOpciones={pregunta.opciones}
            valorDefault={valorInicial}
            numeroPregunta={id}
            esObligatoria={pregunta.obligatoria}
            onChangeRespuesta={alResponder}
            onElegir={onElegir}
            seleccionUnica={true}
          />
        );
      }

      default:
        return null;
    }
  };

  const tituloDe = (pregunta) => (pregunta.titulo || '').trim();

  return (
    <div>
      <div className={classes.paso}>
        Pregunta {paginaActual + 1} de {totalPreguntas}
      </div>
      {/* El key remonta los controles al cambiar de pantalla */}
      <div className={classes.pregunta} key={paginaActual}>
        {pantallaActual.map((pregunta, indice) => {
          const clave = pregunta.clave ?? pregunta.id;
          if (indice === 0) {
            return (
              <div key={clave}>
                <h2>
                  {tituloDe(pregunta)}
                  {pregunta.obligatoria && <span className={classes.req}>&nbsp;*</span>}
                </h2>
                {renderControl(pregunta)}
                {pregunta.obligatoria ? (
                  <div className={classes.notaReq}>
                    <span>*</span> Respuesta obligatoria
                  </div>
                ) : (
                  <div className={classes.opcional}>Opcional</div>
                )}
              </div>
            );
          }
          // Preguntas adicionales
          return (
            <div key={clave} className={classes.extra}>
              <label htmlFor={`pregunta-${clave}`}>
                {tituloDe(pregunta)}
                {pregunta.obligatoria && <span className={classes.req}>&nbsp;*</span>}
              </label>
              {renderControl(pregunta)}
              {!pregunta.obligatoria && <div className={classes.opcional}>Opcional</div>}
            </div>
          );
        })}
      </div>

      <div className={classes.nav}>
        {paginaActual > paginaMinima && (
          <button
            type="button"
            className={`${base.btn} ${base.sec}`}
            onClick={() => {
              clearTimeout(temporizadorAvance.current);
              setPaginaActual(paginaActual - 1);
            }}
          >
            ← Anterior
          </button>
        )}
        <div className={classes.navDerecha}>
          {esUltimaPantalla ? (
            <BotonEnviar
              respuestas={respuestas}
              datosCrudos={datosCrudos || ''}
              habilitarBotonEnviar={habilitarBotonEnviar}
            />
          ) : (
            <button
              type="button"
              className={`${base.btn} ${base.prim}`}
              disabled={!habilitarSiguiente}
              onClick={() => {
                clearTimeout(temporizadorAvance.current);
                avanzar();
              }}
            >
              Siguiente →
            </button>
          )}
        </div>
      </div>

      <div className={classes.progreso}>
        <div className={classes.barras}>
          {pantallas.map((pantalla) => (
            <span key={pantalla[0].id} className={pantallaRespondida(pantalla) ? classes.ok : ''} />
          ))}
        </div>
        <div className={classes.progresoTxt}>
          {pantallasRespondidas === totalPreguntas ? (
            <>
              <b>¡Listo!</b> Respondiste todas las preguntas.
            </>
          ) : (
            <>
              <b>
                {pantallasRespondidas} de {totalPreguntas}
              </b>{' '}
              preguntas respondidas
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Preguntas;
